/**
 * Reusable telemetry-config editor for an existing tenant.
 *
 * Reads /api/v1/control/tenants/{id}/telemetry-config and lets the Track0tic
 * admin pick a business profile (PHARMA / DIAGNOSTIC / BULLION / DEMO / CUSTOM)
 * or toggle individual channels. Saves via PUT to the same endpoint.
 */
import { useEffect, useState, useCallback } from 'react';
import { controlApi } from '@/services/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Pill, Microscope, Gem, Sparkles, SlidersHorizontal, Landmark,
  Thermometer, Droplets, MapPin, PackageOpen, Scale, Save, Loader2,
} from 'lucide-react';

const PROFILE_PRESETS = {
  PHARMA:     { temperature: true,  humidity: true,  gps: true, bag_events: true,  weight: false },
  DIAGNOSTIC: { temperature: true,  humidity: false, gps: true, bag_events: true,  weight: false },
  BULLION:    { temperature: false, humidity: false, gps: true, bag_events: true,  weight: true  },
  BANK:       { temperature: false, humidity: false, gps: true, bag_events: false, weight: false },
  BANK_P2:    { temperature: false, humidity: false, gps: true, bag_events: true,  weight: false },
  DEMO:       { temperature: true,  humidity: true,  gps: true, bag_events: true,  weight: true  },
  CUSTOM:     { temperature: true,  humidity: false, gps: true, bag_events: true,  weight: false },
};

const PROFILES = [
  { id: 'PHARMA',     label: 'Pharma',       icon: Pill },
  { id: 'DIAGNOSTIC', label: 'Diagnostic',   icon: Microscope },
  { id: 'BULLION',    label: 'Bullion',      icon: Gem },
  { id: 'BANK',       label: 'Bank P1',      icon: Landmark },
  { id: 'BANK_P2',    label: 'Bank P2',      icon: Landmark },
  { id: 'DEMO',       label: 'Demo',         icon: Sparkles },
  { id: 'CUSTOM',     label: 'Custom',       icon: SlidersHorizontal },
];

const CHANNELS = [
  { key: 'temperature', label: 'Temperature', icon: Thermometer },
  { key: 'humidity',    label: 'Humidity',    icon: Droplets },
  { key: 'gps',         label: 'GPS',         icon: MapPin },
  { key: 'bag_events',  label: 'Bag Events',  icon: PackageOpen },
  { key: 'weight',      label: 'Weight',      icon: Scale },
];

export default function TelemetryConfigEditor({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState('CUSTOM');
  const [channels, setChannels] = useState(PROFILE_PRESETS.CUSTOM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await controlApi.getTenantTelemetryConfig(tenantId);
      setProfile(res.data.profile_type || 'CUSTOM');
      setChannels(res.data.channels || PROFILE_PRESETS.CUSTOM);
    } catch (err) {
      toast.error('Could not load telemetry config');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const selectProfile = (id) => {
    setProfile(id);
    setChannels({ ...PROFILE_PRESETS[id] });
  };

  const toggle = (key) => {
    const next = { ...channels, [key]: !channels[key] };
    setChannels(next);
    const matches = Object.keys(PROFILE_PRESETS[profile] || {}).every(
      (k) => PROFILE_PRESETS[profile][k] === next[k],
    );
    if (!matches) setProfile('CUSTOM');
  };

  const save = async () => {
    if (!Object.values(channels).some(Boolean)) {
      toast.error('Enable at least one telemetry channel');
      return;
    }
    setSaving(true);
    try {
      await controlApi.updateTenantTelemetryConfig(tenantId, {
        profile_type: profile,
        channels,
      });
      toast.success('Telemetry config saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="telemetry-config-editor">
      <div>
        <p className="text-sm text-slate-400 mb-3">
          Profile drives which telemetry channels this customer can see on their dashboard.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {PROFILES.map((p) => {
            const Icon = p.icon;
            const selected = profile === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => selectProfile(p.id)}
                data-testid={`tc-profile-${p.id.toLowerCase()}`}
                className={cn(
                  'p-3 rounded-lg border text-left transition-all',
                  selected
                    ? 'bg-blue-600/15 border-blue-500/40 text-blue-300'
                    : 'bg-slate-800/40 border-slate-700 text-slate-300 hover:border-slate-500',
                )}
              >
                <Icon className="w-4 h-4 mb-1.5" />
                <p className="text-sm font-medium">{p.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40">
        <div className="px-4 py-2 border-b border-slate-800 text-sm font-semibold text-slate-200">
          Telemetry Channels
        </div>
        <div className="divide-y divide-slate-800">
          {CHANNELS.map((ch) => {
            const Icon = ch.icon;
            const on = !!channels[ch.key];
            return (
              <label
                key={ch.key}
                className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800/40"
              >
                <div className="flex items-center gap-3">
                  <Icon className={cn('w-4 h-4', on ? 'text-blue-400' : 'text-slate-500')} />
                  <span className={cn('text-sm', on ? 'text-white' : 'text-slate-500')}>{ch.label}</span>
                </div>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(ch.key)}
                  className="w-5 h-5 rounded accent-blue-600 cursor-pointer"
                  data-testid={`tc-toggle-${ch.key}`}
                />
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="tc-save-btn" className="bg-blue-600 hover:bg-blue-700">
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save telemetry config</>
          )}
        </Button>
      </div>
    </div>
  );
}
